import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  StudentsDataService,
  Student,
  HomeUnitChange,
  ChurchUnit
} from '../../../shared';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import {
  SelectChurchUnitDialog,
  UnitType,
  ConfirmDialog,
  MessageDialog
} from '../../../shared';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './home-ward-student.component.html',
  styleUrls: ['./home-ward-student.component.scss']
})
export class HomeWardStudentComponent implements OnInit, OnDestroy {
  homeUnitHistoryColumns = ['oldUnit', 'newUnit', 'updatedBy', 'updatedOn'];

  loadingError = false;
  loading = false;
  loadingHomeUnitChanges = false;
  updatingHomeUnit = false;

  student: Student;
  homeUnitChangesDataSource = new MatTableDataSource<HomeUnitChange>();

  private parameterSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private studentService: StudentsDataService,
    private titleService: Title
  ) {
    // subscribe to our route parameter's value for the student's ID
    this.parameterSubscription = this.route.params.subscribe(params => {
      this.loadStudent(params['identifier']);
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.parameterSubscription.unsubscribe();
  }

  /**
   * Uses our service to actually update the student's home unit.
   */
  private updateHomeUnit(newUnit: ChurchUnit): void {
    if (!this.updatingHomeUnit) {
      this.updatingHomeUnit = true;
      this.studentService
        .updateHomeUnit(this.student.iNumber, newUnit)
        .pipe(
          finalize(() => (this.updatingHomeUnit = false))
        )
        .subscribe(
          () => {
            // update our student's home unit to reflect;
            this.student.homeUnit = newUnit;
            // kick off loading our history so that it reflects too
            this.loadHomeUnitChanges();
          },
          error => {
            const errorDialog = this.dialog.open(MessageDialog, {
              data: {
                message: "Unable to update the student's home unit",
                isError: true
              }
            });
          }
        );
    }
  }

  /**
   * Loads the student based on the identifier.
   */
  loadStudent(identifier: string): void {
    this.loading = true;
    this.student = null;
    this.studentService
      .getStudent(identifier)
      .pipe(
        finalize(() => (this.loading = false))
      )
      .subscribe(
        student => {
          this.student = student;
          if (this.student) {
            this.titleService.setTitle(
              `Update home unit: ${this.student.iNumber}`
            );
            this.loadHomeUnitChanges();
          }
        },
        error => {
          this.loadingError = true;
        }
      );
  }

  /**
   * Loads the most recent changes to the student's home unit.
   */
  loadHomeUnitChanges(): void {
    if (this.student) {
      this.homeUnitChangesDataSource.data = null;
      this.loadingHomeUnitChanges = true;
      this.studentService
        .getHomeUnitChanges(this.student.iNumber)
        .pipe(
          finalize(() => (this.loadingHomeUnitChanges = false))
        )
        .subscribe(changes => {
          this.homeUnitChangesDataSource.data = changes;
        });
    }
  }

  /**
   * Opens up the dialog to find a home unit and updates it if a valid one is selected.
   */
  promptUpdateHomeUnit(): void {
    const dialog = this.dialog.open(SelectChurchUnitDialog, {
      data: { validUnitTypes: [UnitType.Branch, UnitType.Ward] }
    });

    dialog.afterClosed().subscribe((selectedUnit: ChurchUnit) => {
      const isDifferentThanCurrent =
        this.student.homeUnit != null
          ? selectedUnit.unitNumber !== this.student.homeUnit.unitNumber
          : true;
      if (selectedUnit && isDifferentThanCurrent) {
        // confirm that they really want to update the student's home unit
        const message = `Are you sure you want to update this student's home unit to ${
          selectedUnit.name
        }?`;
        const confirmDialog = this.dialog.open(ConfirmDialog, {
          autoFocus: false,
          data: { title: 'Please confirm', message: message }
        });

        confirmDialog.afterClosed().subscribe(response => {
          if (response) {
            // actually update the student's home unit
            this.updateHomeUnit(selectedUnit);
          }
        });
      }
    });
  }
}
